import {
  ChatCircle,
  CircleNotch,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import cx from "classnames";

import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../../components/InteractiveSurface";

export type ReviewListCardItem = {
  id: string;
  name: string;
  versionNumber?: number;
  thumbnailUrl?: string | null;
  status?: string;
  aiCommentCount?: number;
  humanCommentCount?: number;
  disabled?: boolean;
  isGenerating?: boolean;
};

/**
 * Shared review card used by project and standalone chat review rails.
 */
export function ReviewListCard({
  item,
  onClick,
  testIdPrefix = "project-review-card",
  "data-testid": dataTestId,
  isActive,
}: {
  item: ReviewListCardItem;
  onClick?: () => void;
  /** Prefix for `data-testid` attributes, e.g. `project-review-card` or `chat-review-card`. */
  testIdPrefix?: string;
  "data-testid"?: string;
  isActive?: boolean;
}) {
  const cardTestId = dataTestId ?? `${testIdPrefix}-${item.id}`;
  const aiCommentCount = item.aiCommentCount ?? 0;
  const humanCommentCount = item.humanCommentCount ?? 0;
  const isGenerating = item.isGenerating ?? false;
  const disabled = item.disabled ?? isGenerating;

  const ariaLabel = isGenerating
    ? `${item.name} generating, please wait`
    : isActive
      ? `Currently viewing ${item.name}`
      : `Open review for ${item.name}${item.status ? ` (${item.status})` : ""}`;

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-current={isActive ? "true" : undefined}
      onClick={() => {
        if (!disabled) onClick?.();
      }}
      aria-label={ariaLabel}
      data-testid={cardTestId}
      className={cx(
        "flex flex-col gap-2 p-3 text-left disabled:cursor-not-allowed disabled:opacity-60",
        interactiveSurfaceClasses({ interactive: true, disabled }),
        isActive && "ring-2 ring-puntt-accent-8 ring-offset-1",
      )}
    >
      <div
        className="flex items-stretch gap-3"
        data-testid={`${testIdPrefix}-${item.id}-row`}
      >
        <span
          aria-hidden="true"
          data-testid={`${testIdPrefix}-${item.id}-thumbnail`}
          className={interactiveSurfaceIconClasses(
            "flex w-10 shrink-0 items-center justify-center self-stretch overflow-hidden rounded-md bg-puntt-red-9 text-base-white",
          )}
        >
          {item.thumbnailUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text -- decorative thumbnail; the card carries an aria-label
            <img
              src={item.thumbnailUrl}
              alt=""
              aria-hidden="true"
              className="size-full object-cover"
            />
          ) : isGenerating ? (
            <CircleNotch
              size={18}
              className="animate-spin"
              aria-hidden="true"
            />
          ) : (
            <ImageIcon size={18} weight="fill" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-puntt-neutral-gray-12">
            {item.name}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2">
            {item.versionNumber != null && (
              <span
                className="font-mono inline-block rounded border border-puntt-neutral-gray-12 bg-puntt-neutral-gray-12 px-1.5 py-px text-[10px] font-medium text-base-white"
                data-testid={`${testIdPrefix}-${item.id}-version`}
              >
                v{item.versionNumber}
              </span>
            )}
            <span
              className="inline-flex items-center gap-1 text-[11px] text-puntt-neutral-gray-10"
              data-testid={`${testIdPrefix}-${item.id}-ai-comments`}
            >
              <ChatCircle size={12} aria-hidden="true" className="shrink-0" />
              {aiCommentCount}{" "}
              {aiCommentCount === 1 ? "AI comment" : "AI comments"}
            </span>
            {humanCommentCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] text-puntt-neutral-gray-10"
                data-testid={`${testIdPrefix}-${item.id}-human-comments`}
              >
                <ChatCircle size={12} aria-hidden="true" className="shrink-0" />
                {humanCommentCount}{" "}
                {humanCommentCount === 1 ? "human comment" : "human comments"}
              </span>
            )}
          </span>
        </span>
      </div>
    </button>
  );
}
